import './styles/SinglePage.scss';

import { ApplianceAccordion, ApplianceImage } from '../components/appliance';
import React, { useState, useMemo } from 'react';

import { AddToCartButtonContainer } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Cart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GetDocDisplayName } from '../utils';
import { Icon } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import Layout from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Layout/Layout';
import LocalePrice from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/LocalePrice';
import SEO from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Seo';
import { faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { graphql } from 'gatsby';
import marked from 'marked';
import { useAvailabilityInfo } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Cart/AddToCartButton';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useProductPrice } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/store';
import { useTranslation } from 'react-i18next';
import { IAppliance } from '../interfaces';
import { Metadata } from '../Models/metadata';

export const query = graphql`
    query SinglePage($code: String!) {
        productsJson(code: { eq: $code }) {
            id
            code
            category
            slug {
                en_CA
                en_US
                fr_CA
            }
            title {
                en_CA
                en_US
                fr_CA
            }
            documents {
                en_CA
                en_US
                fr_CA
                caption {
                    en_CA
                    en_US
                    fr_CA
                }
            }
            images
            schema {
                en_CA
                en_US
                fr_CA
            }
            fields {
                schema {
                    childImageSharp {
                        gatsbyImageData(layout: CONSTRAINED)
                    }
                }
                ...productsFieldsImagesFragment
            }
            category_localized {
                en_CA
                en_US
                fr_CA
            }
            description {
                en_CA
                en_US
                fr_CA
                valuesMarkdownRemark {
                    en_CA
                    en_US
                    fr_CA
                }
            }
        }
    }
`;

export default function SinglePage({
    data,
}: {
    data: Queries.SinglePageQuery;
}) {
    const single = data.productsJson;
    const { t } = useTranslation('pages');
    const { currentLocale } = usePageLocalization();
    const [qtyValue, setQtyValue] = useState(1);

    const category = single?.category?.split('/')[2];

    const availability =
        useAvailabilityInfo(single).availabilityLabel ||
        t('layout:addToCartBtn.not-available');
    const downloadIcon =
        'M21.516,10.507h-4V4.5H11.5v6.007h-4l7.008,7.008ZM7.5,19.517v2H21.516v-2Z';

    // const displayPrice = currentLocale !== 'en_US';
    const displayPrice = true;
    const document = single?.documents?.[0];
    const [price] = useProductPrice(single?.code as string);
    const [isDescOpen, setDescOpen] = useState(true);
    marked.setOptions({
        breaks: true,
        renderer: new marked.Renderer(),
        gfm: true,
    });

    const getMetadata = (category: string, single: any): Metadata => {
        const metadata = {
            metaTitle: '',
            metaDescription: '',
        };

        const sku = single?.code as string;
        const productTitle = single?.title?.[currentLocale] as string;
        const subCategory =
            single?.category_localized?.[currentLocale]?.split('/')[3];
        if (category === 'Parts') {
            metadata.metaTitle = t('metadata:part.title', { SKU: sku });
            metadata.metaDescription = t('metadata:part.description', {
                TITLE: productTitle,
                SKU: sku,
            });
        } else if (category === 'Accessories') {
            metadata.metaTitle = t('metadata:single.title', {
                SKU: sku,
                CATEGORY: subCategory,
            });
            metadata.metaDescription = t('single.description', {
                TITLE: productTitle,
                SKU: sku,
            });
        } else if (category === 'Options') {
            metadata.metaTitle = t('metadata:option.title', { SKU: sku });
            metadata.metaDescription = t('metadata:option.description', {
                TITLE: productTitle,
                SKU: sku,
            });
        } else {
            metadata.metaTitle = '';
            metadata.metaDescription = '';
        }
        return metadata;
    };

    const metadata = useMemo<Metadata>(
        () => getMetadata(category ?? '', single),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentLocale, category]
    );

    return (
        <Layout>
            <SEO
                title={metadata.metaTitle}
                description={metadata.metaDescription}
            />
            <div className='container'>
                <div className='single-page'>
                    <div className='single-infos'>
                        <div className='left'>
                            <ApplianceImage
                                appliance={single as unknown as IAppliance}
                                currentLocale={currentLocale}
                            />
                            {single?.description?.valuesMarkdownRemark?.[
                                currentLocale
                            ] && (
                                <ApplianceAccordion
                                    title={t('venting.description')}
                                    open={isDescOpen}
                                    onClick={() => setDescOpen(!isDescOpen)}
                                >
                                    <div
                                        className='description'
                                        dangerouslySetInnerHTML={{
                                            __html: single.description
                                                .valuesMarkdownRemark?.[
                                                currentLocale
                                            ],
                                        }}
                                    />
                                </ApplianceAccordion>
                            )}
                        </div>
                        <div className='right'>
                            <h1 className='single-title'>
                                <span>
                                    {single?.title?.[currentLocale] ?? 'null'}
                                </span>
                                <br />
                                <span className='code'>{single?.code}</span>
                            </h1>

                            {displayPrice && (
                                <div className='price'>
                                    <span>
                                        {price?.regularPrice?.value === 0
                                            ? '--'
                                            : LocalePrice(
                                                  currentLocale,
                                                  price?.regularPrice?.value
                                              )}
                                    </span>
                                </div>
                            )}
                            <p>{availability}</p>

                            <AddToCartButtonContainer
                                product={single}
                                qtyValue={qtyValue}
                                setQtyValue={setQtyValue}
                            />

                            {document && (
                                <div className='actions'>
                                    <div className='fileicon'>
                                        <FontAwesomeIcon icon={faFileAlt} />
                                    </div>

                                    <div className='download-manual'>
                                        <p className='filename'>
                                            {GetDocDisplayName(
                                                document,
                                                single?.code as string
                                            )}
                                        </p>
                                        <a
                                            rel='noreferrer'
                                            target='_blank'
                                            href={
                                                '/file/' +
                                                document[currentLocale]
                                            }
                                            className='download-text'
                                        >
                                            {t('venting.download-manual')}
                                            <Icon
                                                d={downloadIcon}
                                                size='20'
                                                viewbox='0 0 14.016 17.019'
                                                transform='translate(-7.5 -4.5)'
                                                color='#B2B2B2'
                                            />
                                        </a>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
